import { PureComponent } from 'react'
import GatherHeader from 'components/features/pfm/GatherHeader/GatherHeader'
import Anchor from 'components/atoms/navigation/Anchor'
import { REDEMPTION_PAGE } from 'constants/pages'
import RedemptionFormContainer from 'components/features/pfm/RedemptionForm/RedemptionFormContainer'
import { Redirect, RouteComponentProps } from 'react-router'
import { REWARDS_URL, DASHBOARD_URL } from 'tangle-shared'
import { RedemptionStepNumber, RedemptionStepName } from 'interfaces/bounty'
import PageLoadingOverlay from 'components/atoms/preloaders/PageLoadingOverlay'
import ProgressBar from 'components/features/pfm/ProgressBar/ProgressBar'
import {
  REDEMPTION_STEP_NUMBER_BY_STEP_NAME,
  REDEMPTION_AMOUNT_STEP_NUMBER,
  REDEMPTION_CONFIRM_STEP_NUMBER,
  REDEMPTION_SUCCESS_STEP_NUMBER,
  REDEMPTION_LAST_STEP_NUMBER,
} from 'constants/bounty'
import CommonFooter from 'components/molecules/CommonFooter'
import InternalSidebarContainer from 'components/features/pfm/InternalSidebar/InternalSidebarContainer'
import * as styles from 'components/features/pfm/RedemptionPage/RedemptionPage.css'
import { PageType } from 'interfaces/pages'
import { getRedemptionFormUrl } from 'helpers/bountyHelpers'
import InternalLayoutContainer from 'components/features/pfm/InternalLayout/InternalLayoutContainer'
import { NotificationTrayWrapper } from 'components/features/pfm/NotificationTray/NotificationTrayWrapper'

const getTitleByStep = (stepNumber: RedemptionStepNumber) => ([
  'Redeem your rewards',
  'Where should we send your money?',
  'Confirm your redemption',
  'Your redemption is on it’s way',
][stepNumber])

const getSubtitleByStep = (stepNumber: RedemptionStepNumber, accountNumber4?: string) => ([
  `How much would you like to redeem?`,
  `Please enter the bank account you would like your redemption transferred to.`,
  `This transfer can’t be undone. Please verify the redemption details before submitting.`,
  `Your rewards are being transferred to your bank account ending in ${ accountNumber4 }.`,
][stepNumber])

export type RedemptionPageStateProps = {
  isMobile: boolean
  isLoading: boolean
  isFormSubmitting: boolean
  isRedemptionFormCompleted: boolean
  redemptionStepNumber: RedemptionStepNumber
  account_number: string
}

type RedemptionPageProps = RedemptionPageStateProps & RouteComponentProps<{ step: RedemptionStepName }>

class RedemptionPage extends PureComponent<RedemptionPageProps> {
  render () {
    const stepNumber = this.getCurrentStepNumber()
    if (stepNumber === undefined) {
      return (
        <Redirect to={ getRedemptionFormUrl(REDEMPTION_AMOUNT_STEP_NUMBER) } />
      )
    } else if (stepNumber !== REDEMPTION_LAST_STEP_NUMBER && this.props.redemptionStepNumber === REDEMPTION_LAST_STEP_NUMBER) {
      return (
        <Redirect to={ getRedemptionFormUrl(REDEMPTION_LAST_STEP_NUMBER) } />
      )
    } else if (stepNumber > this.props.redemptionStepNumber) {
      return (
        <Redirect to={ getRedemptionFormUrl(this.props.redemptionStepNumber) } />
      )
    }

    const notifications = (
      <NotificationTrayWrapper
        renderLevel='page'
        isPositionRelative
      />
    )

    return (
      <InternalLayoutContainer
        sidebar={ this.renderSidebar() }
        notifications={ notifications }
        isFooterHidden
      >
        <Anchor placement={ REDEMPTION_PAGE }>
          <div className={ styles.page }>
            <div className={ styles.content }>
              { this.renderHeader() }
              { this.renderForm() }
            </div>
          </div>
          <footer className={ styles.footer }>
            <CommonFooter />
          </footer>
          { this.renderLoadingOverlay() }
        </Anchor>
      </InternalLayoutContainer>
    )
  }

  renderSidebar () {
    const stepNumber = this.getCurrentStepNumber()
    const progressPerStep = Math.ceil(100 / REDEMPTION_LAST_STEP_NUMBER)
    const progress = Math.min(100, stepNumber * progressPerStep)

    const { isFormSubmitting, isLoading } = this.props

    let backPage: PageType | null | undefined
    if (isFormSubmitting) {
      backPage = null
    } else if (isLoading || stepNumber === REDEMPTION_LAST_STEP_NUMBER) {
      backPage = 'rewards'
    }

    return (
      <InternalSidebarContainer backPage={ backPage }>
        <div className={ styles.progressBar }>
          <ProgressBar progress={ progress } />
        </div>
      </InternalSidebarContainer>
    )
  }

  renderHeader () {
    const { isMobile, isLoading, account_number } = this.props
    if (isLoading) {
      return null
    }

    const stepNumber = this.getCurrentStepNumber()
    const title = getTitleByStep(stepNumber)
    const subtitle = getSubtitleByStep(stepNumber, account_number.slice(-4))

    return (
      <GatherHeader
        title={ title }
        step={ isMobile ? undefined : stepNumber + 1 }
        maxStep={ REDEMPTION_LAST_STEP_NUMBER }
      >
        <h4 className={ styles.subtitle }>{ subtitle }</h4>
      </GatherHeader>
    )
  }

  renderForm () {
    const stepNumber = this.getCurrentStepNumber()
    return (
      <RedemptionFormContainer
        stepNumber={ stepNumber }
        onStepChange={ this.handleStepChange }
      />
    )
  }

  renderLoadingOverlay () {
    if (!this.props.isLoading) {
      return null
    }

    return (
      <PageLoadingOverlay />
    )
  }

  componentDidMount () {
    if (this.props.isRedemptionFormCompleted) {
      this.props.history.push(REWARDS_URL)
      this.props.history.push(DASHBOARD_URL)
    }
  }

  componentDidUpdate (prevProps: RedemptionPageProps) {
    if (prevProps.redemptionStepNumber === REDEMPTION_CONFIRM_STEP_NUMBER
      && this.props.redemptionStepNumber === REDEMPTION_SUCCESS_STEP_NUMBER) {
      this.props.history.push(getRedemptionFormUrl(REDEMPTION_SUCCESS_STEP_NUMBER))
    }
  }

  private getCurrentStepNumber () {
    const stepName = this.props.match.params.step
    return REDEMPTION_STEP_NUMBER_BY_STEP_NAME[stepName]
  }

  private handleStepChange = (nextStepNumber: RedemptionStepNumber) => {
    this.props.history.push(getRedemptionFormUrl(nextStepNumber))
  }
}

export default RedemptionPage
